import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { nichesData } from '@/mocks/niches';
import { useScrollReveal, useScrollRevealGroup } from '@/hooks/useScrollReveal';

export default function NicheDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const niche = useMemo(() => nichesData.find((n) => n.slug === slug), [slug]);

  const { ref: heroLabelRef, revealed: heroLabelRevealed } = useScrollReveal();
  const { ref: heroTitleRef, revealed: heroTitleRevealed } = useScrollReveal({ delay: 100 });
  const { ref: heroSubRef, revealed: heroSubRevealed } = useScrollReveal({ delay: 200 });
  const { ref: heroBtnRef, revealed: heroBtnRevealed } = useScrollReveal({ delay: 350 });

  const { ref: howLabelRef, revealed: howLabelRevealed } = useScrollReveal();
  const { ref: howTitleRef, revealed: howTitleRevealed } = useScrollReveal({ delay: 100 });
  const { containerRef: stepsRef, revealedCount: stepsRevealed } = useScrollRevealGroup<HTMLDivElement>(3);

  const { ref: whyLabelRef, revealed: whyLabelRevealed } = useScrollReveal();
  const { ref: whyTitleRef, revealed: whyTitleRevealed } = useScrollReveal({ delay: 100 });
  const { containerRef: whyRef, revealedCount: whyRevealed } = useScrollRevealGroup<HTMLDivElement>(4);

  const { ref: ctaRef, revealed: ctaRevealed } = useScrollReveal();

  useEffect(() => {
    if (!niche) return;
    document.title = niche.seoTitle;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const upsertJsonLd = (id: string, json: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.setAttribute('type', 'application/ld+json');
        el.setAttribute('id', id);
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(json);
    };

    setMeta('description', niche.seoDescription);
    setMeta('keywords', niche.seoKeywords);
    setMeta('og:title', niche.seoTitle, true);
    setMeta('og:description', niche.seoDescription, true);
    setMeta('og:url', `https://aigam.ru/niches/${slug}`, true);
    setMeta('og:type', 'website', true);
    setMeta('canonical', `https://aigam.ru/niches/${slug}`);

    upsertJsonLd('niche-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://aigam.ru/' },
        { '@type': 'ListItem', position: 2, name: 'Заявки под ключ', item: 'https://aigam.ru/leads-turnkey' },
        { '@type': 'ListItem', position: 3, name: niche.name, item: `https://aigam.ru/niches/${slug}` },
      ],
    });

    upsertJsonLd('niche-service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: niche.heroHeadline,
      provider: {
        '@type': 'Organization',
        name: 'AIGAM',
        url: 'https://aigam.ru',
      },
      description: niche.seoDescription,
      areaServed: { '@type': 'Country', name: 'Россия' },
      offers: {
        '@type': 'Offer',
        price: niche.price.replace(/[^\d]/g, '').split('').slice(0, 4).join(''),
        priceCurrency: 'RUB',
      },
    });

    return () => {
      document.title = 'AIGAM';
    };
  }, [niche, slug]);

  if (!niche) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground-950">Ниша не найдена</h2>
          <p className="mt-3 text-foreground-500">Такой страницы не существует.</p>
          <Link
            to="/leads-turnkey"
            className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Вернуться к списку ниш
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main className="bg-background-50">
      <Navbar />
      {/* Hero */}
      <section className="relative w-full min-h-[85vh] flex items-center bg-background-50 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: 'radial-gradient(circle, oklch(var(--primary-500)) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        <div className="absolute top-24 right-10 w-[450px] h-[450px] opacity-[0.05]" style={{ background: 'radial-gradient(circle at center, oklch(var(--primary-500)), transparent 70%)' }} />
        <div className="absolute bottom-16 left-0 w-[350px] h-[350px] opacity-[0.04]" style={{ background: 'radial-gradient(circle at center, oklch(var(--accent-500)), transparent 70%)' }} />

        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-10 pt-28 md:pt-32 pb-12 md:pb-16">
          <span
            ref={heroLabelRef}
            className={`inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-[12px] font-semibold uppercase tracking-[0.12em] reveal-fade-up ${heroLabelRevealed ? 'revealed' : ''}`}
          >
            {niche.name}
          </span>

          <h1
            ref={heroTitleRef}
            className={`mt-4 md:mt-5 font-display font-extrabold text-[32px] sm:text-[40px] md:text-[58px] leading-[1.07] tracking-tightest text-foreground-950 max-w-3xl reveal-fade-up ${heroTitleRevealed ? 'revealed' : ''}`}
          >
            {niche.heroHeadline}
          </h1>

          <p
            ref={heroSubRef}
            className={`mt-4 md:mt-6 text-[15px] md:text-[18px] text-foreground-500 max-w-2xl leading-relaxed reveal-fade-up ${heroSubRevealed ? 'revealed' : ''}`}
          >
            {niche.heroSubheadline}
          </p>

          <div
            ref={heroBtnRef}
            className={`mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 reveal-fade-up ${heroBtnRevealed ? 'revealed' : ''}`}
          >
            <a
              href={niche.telegramLink}
              target="_blank"
              rel="noopener noreferrer"
              className="h-12 px-7 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-[15px] font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-telegram-fill text-lg"></i>
              Получить заявки в Telegram
            </a>
            <span className="text-[14px] text-foreground-500">
              Средняя цена заявки: <strong className="text-foreground-900">{niche.price}</strong>
            </span>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-14 md:py-24 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-10">
          <div className="text-center mb-10 md:mb-16">
            <span
              ref={howLabelRef}
              className={`inline-block text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-600 reveal-fade-up ${howLabelRevealed ? 'revealed' : ''}`}
            >
              Как мы работаем
            </span>
            <h2
              ref={howTitleRef}
              className={`mt-3 font-display font-extrabold text-[28px] md:text-[38px] leading-[1.15] tracking-tightest text-foreground-950 reveal-fade-up ${howTitleRevealed ? 'revealed' : ''}`}
            >
              Три шага до первой заявки
            </h2>
          </div>

          <div ref={stepsRef} className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
            {niche.howWeWork.map((step, i) => (
              <div
                key={step.step}
                className={`relative bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-7 reveal-fade-up ${i < stepsRevealed ? 'revealed' : ''}`}
                style={i < stepsRevealed ? { transitionDelay: `${i * 120}ms` } : undefined}
              >
                <span className="text-[11px] font-bold text-primary-500 uppercase tracking-[0.2em]">{step.step}</span>
                <h3 className="mt-2 font-display font-bold text-[17px] text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-[14px] text-foreground-500 leading-relaxed">{step.description}</p>

                {i < niche.howWeWork.length - 1 && (
                  <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                    <span className="w-8 h-8 rounded-full bg-background-100 border border-background-200 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-foreground-400 text-sm"></i>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-14 md:py-24 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-10">
          <div className="mb-10 md:mb-16">
            <span
              ref={whyLabelRef}
              className={`inline-block text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-600 reveal-fade-up ${whyLabelRevealed ? 'revealed' : ''}`}
            >
              Почему мы
            </span>
            <h2
              ref={whyTitleRef}
              className={`mt-3 font-display font-extrabold text-[28px] md:text-[38px] leading-[1.15] tracking-tightest text-foreground-950 max-w-2xl reveal-fade-up ${whyTitleRevealed ? 'revealed' : ''}`}
            >
              Вы платите только за результат
            </h2>
          </div>

          <div ref={whyRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 max-w-3xl">
            {niche.whyUs.map((item, i) => (
              <div
                key={item}
                className={`flex items-start gap-3 p-5 rounded-xl bg-background-100 border border-background-200/50 reveal-fade-up ${i < whyRevealed ? 'revealed' : ''}`}
                style={i < whyRevealed ? { transitionDelay: `${i * 80}ms` } : undefined}
              >
                <span className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-check-line text-primary-500 text-[10px]"></i>
                </span>
                <span className="text-[14px] text-foreground-700 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-24 bg-background-900">
        <div
          ref={ctaRef}
          className={`max-w-[1280px] mx-auto px-4 md:px-10 text-center reveal-fade-up ${ctaRevealed ? 'revealed' : ''}`}
        >
          <h2 className="font-display font-extrabold text-[26px] md:text-[38px] leading-[1.15] tracking-tightest text-background-50">
            Готовы получать заявки для {niche.name.toLowerCase()}?
          </h2>
          <p className="mt-4 text-[16px] text-background-50/60 max-w-xl mx-auto leading-relaxed">
            Напишите в Telegram — обсудим вашу нишу, согласуем критерии и запустим поток заявок через 48 часов.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={niche.telegramLink}
              target="_blank"
              rel="noopener noreferrer"
              className="h-12 px-7 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-[15px] font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-telegram-fill text-lg"></i>
              Написать в Telegram
            </a>
            <Link
              to="/leads-turnkey"
              className="h-12 px-7 inline-flex items-center gap-2 rounded-md border border-background-50/20 text-background-50/70 hover:text-background-50 hover:border-background-50/40 text-[15px] font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Все ниши
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}